import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, ShieldCheck, Award, MessageSquare, Building2, Star } from "lucide-react";
import Seo, { localBusinessSchema, websiteSchema } from "../components/Seo";
import { Reveal, Overline, FrameReveal } from "../components/Reveal";
import CtaBand from "../components/CtaBand";
import RelatedInsights from "../components/RelatedInsights";
import { useSettings, useServices, useProjects, useTestimonials, useAreas } from "../lib/hooks";
import { HERO_IMG, TEAM_IMG, categoryLabel } from "../lib/site";
import { track } from "../lib/api";

const TRUST = [
  { icon: ShieldCheck, label: "Fully Insured" },
  { icon: Award, label: "10-Year Workmanship Guarantee" },
  { icon: MessageSquare, label: "Free Initial Consultation" },
  { icon: Building2, label: "Residential Design & Build Specialists" },

];

const VALUES = [
  ["01", "Quality", "Workmanship we are prepared to guarantee for ten years — because we do not expect to be called back."],
  ["02", "Reliability", "Programmes we keep, sites we keep clean, and promises we keep. The basics, done properly."],
  ["03", "Transparency", "Detailed, itemised proposals before work begins. Changes agreed in writing, never surprises."],
  ["04", "Communication", "You will always know what is happening on your project, this week and next."],
  ["05", "Attention to Detail", "The last five per cent is what you live with. We care about it disproportionately."],
  ["06", "Respect", "For your home, your neighbours and your routine. A NOOR site is a considerate site."],
];

const isPrerenderHydration = () =>
  typeof window !== "undefined" && window.__NOOR_PRERENDERED__ === true;


const FiveStars = ({ testid }) => (
  <div className="flex items-center gap-1" data-testid={testid} aria-label="Five star review">
    {[0, 1, 2, 3, 4].map((s) => (
      <Star key={s} size={14} className="text-[#D4A017]" fill="#D4A017" strokeWidth={0} />
    ))}
  </div>
);

function ReviewSlider({ testimonials }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = testimonials.length;

  useEffect(() => {
    if (paused || n < 2) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % n), 6000);
    return () => clearInterval(id);
  }, [paused, n]);

  const t = testimonials[index];

  return (
    <div
      className="mt-14 max-w-3xl"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="review-slider"
    >
      <AnimatePresence mode="wait">
        <motion.figure
          key={index}
          initial={isPrerenderHydration() ? false : { opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
          drag={n > 1 ? "x" : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.2}
          onDragEnd={(e, info) => {
            if (info.offset.x < -60) setIndex((i) => (i + 1) % n);
            else if (info.offset.x > 60) setIndex((i) => (i - 1 + n) % n);
          }}
          className="cursor-grab active:cursor-grabbing touch-pan-y"
          data-testid={`testimonial-${index}`}
        >
          <FiveStars testid={`testimonial-stars-${index}`} />
          <blockquote className="serif-accent text-graphite/85 leading-relaxed text-xl md:text-2xl lg:text-[1.7rem] mt-6 min-h-[120px] md:min-h-[150px]">
            “{t.text}”
          </blockquote>
          <figcaption className="mt-8 flex items-center gap-4">
            <span className="w-10 h-px bg-ember" />
            <div>
              <p className="font-display font-medium">{t.name}</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-graphite/45 mt-0.5">
                {[t.location, t.project_type].filter(Boolean).join(" · ")}
              </p>
            </div>
          </figcaption>
        </motion.figure>
      </AnimatePresence>

      {n > 1 && (
        <div className="mt-10 flex items-center gap-6">
          <div className="flex items-center gap-2.5" data-testid="review-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Show review ${i + 1}`}
                data-testid={`review-dot-${i}`}
                className={`h-1.5 transition-all duration-300 ${i === index ? "w-8 bg-ember" : "w-1.5 bg-graphite/25 hover:bg-graphite/50"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={() => setIndex((i) => (i - 1 + n) % n)}
              aria-label="Previous review"
              data-testid="review-prev"
              className="w-10 h-10 border border-graphite/20 hover:border-ember hover:text-ember flex items-center justify-center transition-colors duration-300"
            >
              <ArrowRight size={14} className="rotate-180" />
            </button>
            <button
              type="button"
              onClick={() => setIndex((i) => (i + 1) % n)}
              aria-label="Next review"
              data-testid="review-next"
              className="w-10 h-10 border border-graphite/20 hover:border-ember hover:text-ember flex items-center justify-center transition-colors duration-300"
            >
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


const HOME_SERVICES = [
  { slug: "house-extensions", label: "Extensions" },
  { slug: "new-builds", label: "New Builds" },
  { slug: "loft-conversions", label: "Conversions" },
  { slug: "full-home-renovations", label: "Renovations" },
];

const ETHOS = ["Quality you can see", "Service you can trust", "Fully insured", "10-year workmanship guarantee", "Bristol · Bath · Somerset & Surrounding Areas"];

// Full-bleed photographic backdrop: two genuine NOOR project photographs in a
// slow crossfade with a gentle CSS Ken Burns drift. Class-based animation only
// (no inline motion styles) so prerendered snapshots and hydration always agree.
const HERO_STILLS = ["/api/uploads/brand/hero-still-2.webp", "/api/uploads/brand/hero-still-1.webp"];

function HeroBackdrop() {
  const [active, setActive] = useState(0);
  useEffect(() => {
    if (typeof window !== "undefined" && window.__NOOR_PRERENDER_CAPTURE__) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % HERO_STILLS.length), 6500);
    return () => clearInterval(id);
  }, []);
  return (

    <div className="absolute inset-0" aria-hidden="true" data-testid="hero-backdrop">
      {HERO_STILLS.map((src, i) => (
        <img
          key={src}
          src={src}
          alt=""
          width="2000"
          height="1334"
          loading={i === 0 ? "eager" : "lazy"}
          fetchPriority={i === 0 ? "high" : "low"}
          draggable={false}
          className={`absolute inset-0 w-full h-full object-cover slider-drift crossfade-hero ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        />
      ))}
    </div>
  );
}

// Frameless full-bleed gallery slider: seven genuine NOOR interior photographs
// supplied by the owner. Class-based crossfade + Ken Burns drift only (no inline
// motion styles) so prerendered snapshots and client hydration always agree.
// Desktop (md+) uses wide landscape crops the owner supplied separately;
// mobile keeps the original photos 100% unchanged.
const SLIDER_PHOTOS = [
  { src: "/api/uploads/brand/home-slider-3.webp", desktopSrc: "/api/uploads/brand/home-slider-d1.webp", alt: "Elegant media room with brass chandelier finished by NOOR" },
  { src: "/api/uploads/brand/home-slider-4.webp", desktopSrc: "/api/uploads/brand/home-slider-d2.webp", alt: "Dining room with decorative screens and statement lighting by NOOR" },
  { src: "/api/uploads/brand/home-slider-5.webp", desktopSrc: "/api/uploads/brand/home-slider-d3.webp", alt: "Luxury games room interior completed by NOOR Design & Build" },
  { src: "/api/uploads/brand/home-slider-6.webp", desktopSrc: "/api/uploads/brand/home-slider-d4.webp", alt: "Formal dining room with crystal chandeliers by NOOR Design & Build" },
  { src: "/api/uploads/brand/home-slider-7.webp", desktopSrc: "/api/uploads/brand/home-slider-d5.webp", alt: "Panelled lounge with illuminated ceiling feature by NOOR" },
  { src: "/api/uploads/brand/home-slider-1.webp", desktopSrc: "/api/uploads/brand/home-slider-d6.webp", alt: "Bespoke oak and marble kitchen designed and built by NOOR" },
  { src: "/api/uploads/brand/home-slider-2.webp", desktopSrc: "/api/uploads/brand/home-slider-d7.webp", alt: "Restored staircase with dark balustrade in a NOOR renovation" },
];

function HomeGallerySlider() {
  const [active, setActive] = useState(0);
  const n = SLIDER_PHOTOS.length;

  useEffect(() => {
    if (typeof window !== "undefined" && window.__NOOR_PRERENDER_CAPTURE__) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % n), 5200);
    return () => clearInterval(id);
  }, [n]);

  return (
    <section data-testid="home-gallery-slider" className="relative bg-obsidian overflow-hidden" aria-label="NOOR interior photography">
      <div className="relative h-[68vh] md:h-[82vh] w-full">
        {SLIDER_PHOTOS.map((photo, i) => (
          <picture key={photo.src} className="contents">
            <source media="(min-width: 768px)" srcSet={photo.desktopSrc} />
            <img
              src={photo.src}
              alt={photo.alt}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "low"}
              draggable={false}
              data-testid={`home-slider-image-${i}`}
              className={`absolute inset-0 w-full h-full object-cover slider-drift crossfade-slow ${
                i === active ? "opacity-100" : "opacity-0"
              }`}
            />
          </picture>
        ))}
        {/* soft edge vignette so the frameless image settles into the page */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-obsidian/35 via-transparent to-obsidian/45" aria-hidden="true" />
      </div>
    </section>
  );
}

export default function Home() {
  const { data: settings } = useSettings();
  const { data: services } = useServices();
  const { data: featured } = useProjects({ featured: true });
  const { data: testimonials } = useTestimonials();
  const { data: areas } = useAreas();
  const heroRef = useRef(null);

  return (
    <div data-testid="home-page">
      <Seo
        title={settings?.default_seo_title}
        description={settings?.default_seo_description}
        path="/"
        image={HERO_IMG.startsWith("/") ? `https://www.noordb.co.uk${HERO_IMG}` : HERO_IMG}
        schema={{
          "@context": "https://schema.org",
          "@graph": [
            { ...localBusinessSchema(settings, testimonials) },
            { ...websiteSchema(settings) },
          ].map(({ "@context": _c, ...rest }) => rest),
        }}
      />

      {/* ---------------- HERO — full-bleed photographic (video removed at owner's request) ---------------- */}
      <section ref={heroRef} data-testid="hero-section" className="relative bg-graphite text-white overflow-hidden min-h-[100svh] flex flex-col">
        <HeroBackdrop />
        {/* legibility gradients */}
        <div className="absolute inset-0 bg-gradient-to-r from-graphite/85 via-graphite/45 to-graphite/10" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-t from-graphite/90 via-transparent to-graphite/30" aria-hidden="true" />
        <div className="relative flex-1 flex flex-col justify-end max-w-[1440px] w-full mx-auto px-5 md:px-10 pt-28 md:pt-40 pb-10 md:pb-14">
          <motion.p
            initial={isPrerenderHydration() ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.6 }}
            className="font-mono text-[10px] md:text-xs tracking-[0.35em] uppercase text-white/70"
          >
            <span className="text-ember">NOOR</span> — Design &amp; Build in Bristol, Bath, Somerset, Devon &amp; the Surrounding Areas
          </motion.p>
          <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.02] mt-6 max-w-3xl">
            <span className="block">Thoughtfully designed.</span>
            <span className="block serif-accent text-ember text-[1.08em]">Expertly built.</span>
          </h1>
          <p
            data-testid="hero-description"
            className="text-white/75 text-base md:text-lg leading-relaxed mt-7 max-w-xl"
          >
            House extensions, renovations and new homes across Bristol, Bath, Somerset and Devon — spaces
            full of light, designed and built by one owner-led team, from first sketch to final handover.
          </p>
          <motion.div
            initial={isPrerenderHydration() ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 mt-9"
          >
            <Link
              to="/request-a-consultation"
              data-testid="hero-consultation-button"
              className="btn-sheen group inline-flex items-center justify-center gap-3 bg-ember hover:bg-emberdark text-white font-semibold tracking-[0.15em] uppercase text-sm px-8 py-4 transition-colors duration-300"
            >
              Discuss Your Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/cost-calculator"
              data-testid="hero-calculator-button"
              className="inline-flex items-center justify-center gap-3 border border-white/35 hover:border-ember text-white/90 hover:text-ember font-semibold tracking-[0.15em] uppercase text-sm px-8 py-4 transition-colors duration-300"
            >
              Estimate Your Cost
            </Link>
            <Link
              to="/projects"
              data-testid="hero-work-button"
              className="inline-flex items-center justify-center gap-3 text-white/85 hover:text-ember font-mono text-xs tracking-[0.25em] uppercase px-2 py-4 transition-colors duration-300"
            >
              View Our Work <ArrowUpRight size={15} />
            </Link>
          </motion.div>
          {testimonials && testimonials.length > 0 && settings?.google_reviews_url && (
            <motion.a
              href={settings.google_reviews_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-google-rating-badge"
              initial={isPrerenderHydration() ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
              className="group inline-flex items-center gap-3 mt-9 border border-white/25 bg-graphite/40 backdrop-blur-sm hover:border-[#D4A017]/60 px-5 py-3 transition-colors duration-300 w-fit"
            >
              <span className="flex items-center gap-1">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} size={13} className="text-[#D4A017]" fill="#D4A017" strokeWidth={0} />
                ))}
              </span>
              <span className="text-xs md:text-sm text-white/85 font-medium tracking-wide">
                Rated <span className="text-[#D4A017] font-semibold">5.0</span> on Google
              </span>
            </motion.a>
          )}
        </div>

        <motion.div
          initial={isPrerenderHydration() ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.6 }}
          className="relative border-t border-white/15 bg-graphite/30 backdrop-blur-sm"
        >
          <div className="max-w-[1440px] mx-auto grid grid-cols-2 lg:grid-cols-4">
            {TRUST.map(({ icon: Icon, label }, i) => (
              <div
                key={label}
                data-testid={`trust-point-${i}`}
                className="flex items-center gap-3 px-5 md:px-8 py-5 border-r last:border-r-0 border-white/10 [&:nth-child(2)]:border-r-0 lg:[&:nth-child(2)]:border-r"
              >
                <Icon size={18} strokeWidth={1.25} className="text-ember shrink-0" />
                <span className="text-xs md:text-sm text-white/85 font-medium leading-snug">{label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <div className="bg-obsidian border-y border-white/10 py-4 marquee-elegant overflow-hidden w-full" aria-hidden="true">
        <Marquee speed={30} gradient={false}>
          {ETHOS.map((t) => (
            <span key={t} className="flex items-center font-mono text-xs tracking-[0.35em] uppercase text-white/40">
              <span className="px-8">{t}</span>
              <span className="w-1.5 h-1.5 bg-ember inline-block" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* ---------------- MANIFESTO / INTRO ---------------- */}
      <section data-testid="intro-section" className="bg-cream blueprint-grid-light">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>[ 01 — Who We Are ]</Overline>
              <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tighter mt-6 leading-[1.08]">
                One accountable construction partner, from first conversation to completion.
              </h2>
              <p className="text-graphite/60 text-base md:text-lg leading-relaxed mt-8">
                NOOR Design &amp; Build manages carefully planned residential building projects — from the initial
                conversation, through design, consents and construction, to a proper handover. You deal with one
                team that is answerable for everything, rather than juggling trades, designers and suppliers
                yourself.
              </p>
              <Link
                to="/about"
                data-testid="intro-about-link"
                className="group inline-flex items-center gap-3 mt-8 text-sm font-semibold tracking-wide text-graphite hover:text-ember transition-colors duration-300"
              >
                More about NOOR <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 border-t border-l border-black/10">
            {VALUES.map(([num, title, text], i) => (
              <Reveal key={num} delay={i * 0.06} className="border-r border-b border-black/10">
                <div className="p-6 md:p-8 h-full">
                  <p className="font-mono text-xs text-ember">[ {num} ]</p>
                  <h3 className="font-display font-medium text-lg mt-3">{title}</h3>
                  <p className="text-sm text-graphite/55 leading-relaxed mt-2">{text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- GALLERY SLIDER — genuine NOOR interiors (owner-supplied) ---------------- */}
      <HomeGallerySlider />

      {/* ---------------- SERVICES ---------------- */}
      <section data-testid="services-section" className="bg-charcoal text-white">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-32">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Overline light>[ 02 — What We Do ]</Overline>
              <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tighter mt-6">
                Residential design &amp; build, done properly.
              </h2>
            </div>
            <Link to="/services" data-testid="services-view-all-link" className="group inline-flex items-center gap-3 text-sm font-semibold text-white/70 hover:text-ember transition-colors duration-300">
              All services <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-3xl" data-testid="services-intro-paragraph">
              As a residential construction company and design-and-build team, NOOR takes on bespoke
              extensions, complete renovations, conversions and new-build homes — every project managed
              end to end by one accountable team, from design, planning and structural work through
              construction to a finish you will be proud of.
            </p>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10 mt-14">
            {HOME_SERVICES.map(({ slug, label }, i) => {
              const s = (services || []).find((x) => x.slug === slug);
              if (!s) return null;
              return (
                <Reveal key={slug} delay={(i % 2) * 0.08}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    className="group block bg-charcoal hover:bg-graphite p-8 md:p-10 h-full transition-colors duration-500"
                  >
                    <p className="font-mono text-xs text-ember">[ {String(i + 1).padStart(2, "0")} ]</p>
                    <h3 className="font-display font-medium text-xl mt-4 group-hover:text-ember transition-colors duration-300">
                      {label}
                    </h3>
                    <p className="text-sm text-white/50 leading-relaxed mt-3">{s.summary}</p>
                    <span className="inline-flex items-center gap-2 mt-6 text-xs font-mono tracking-[0.2em] uppercase text-white/40 group-hover:text-white transition-colors duration-300">
                      Explore <ArrowUpRight size={13} />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
            <Reveal delay={0.16}>
              <Link
                to="/request-a-consultation"
                data-testid="service-card-consultation"
                className="group flex flex-col justify-between bg-ember hover:bg-emberdark p-8 md:p-10 h-full min-h-[220px] transition-colors duration-500"
              >
                <h3 className="font-display font-medium text-xl leading-snug">Not sure which service fits your project?</h3>
                <span className="inline-flex items-center gap-2 mt-6 text-xs font-mono tracking-[0.2em] uppercase">
                  Ask us directly <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------------- FEATURED PROJECTS (hidden until genuine NOOR case studies are ready) ---------------- */}
      {featured && featured.length > 0 && (
      <section data-testid="featured-projects-section" className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-32">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <Overline>[ 03 — Selected Work ]</Overline>
              <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tighter mt-6">Featured projects.</h2>
            </div>
            <Link to="/projects" data-testid="projects-view-all-link" className="group inline-flex items-center gap-3 text-sm font-semibold text-graphite/70 hover:text-ember transition-colors duration-300">
              All projects <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-14">
            {/* Region-diverse mix so Bath/Somerset work isn't an afterthought */}
            {(() => {
              const sorted = [...(featured || [])].sort((a, b) => (a.home_order ?? a.order ?? 999) - (b.home_order ?? b.order ?? 999));
              const picked = [];
              const seenRegions = new Set();
              for (const p of sorted) {
                if (!seenRegions.has(p.region)) { picked.push(p); seenRegions.add(p.region); }
                if (picked.length === 3) break;
              }
              for (const p of sorted) {
                if (picked.length === 3) break;
                if (!picked.includes(p)) picked.push(p);
              }
              return picked;
            })()
              .map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link to={`/projects/${p.slug}`} data-testid={`featured-project-${p.slug}`} className="group block transition-transform duration-500 ease-out hover:-translate-y-1.5">
                  <div className="border border-black/10 p-2 transition-colors duration-500 group-hover:border-ember/60">
                    <div className="relative overflow-hidden">
                      <FrameReveal src={p.cover} alt={`${p.title} — ${p.subtitle}.`} className="aspect-[4/3]" />
                      <div className="absolute inset-0 bg-graphite/0 group-hover:bg-graphite/25 max-md:bg-graphite/25 transition-colors duration-500 flex items-center justify-center">
                        <span data-testid={`featured-view-project-overlay-${p.slug}`} className="font-mono text-[10px] tracking-[0.35em] uppercase text-white opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 max-md:opacity-100 max-md:translate-y-0 transition-all duration-500 border border-white/40 px-4 py-2">
                          View Project
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display font-medium text-xl">{p.title}</h3>
                      <p className="text-sm text-graphite/55 mt-1">{p.subtitle}</p>
                      {p.placeholder && (
                        <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-graphite/45 mt-2">
                          Design concept — illustrative imagery
                        </p>
                      )}
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-graphite/40 shrink-0 mt-1.5">
                      {(p.categories || []).slice(0, 2).map(categoryLabel).join(" · ")}
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section data-testid="testimonials-section" className="bg-cream">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-32">
          <Reveal>
            <Overline>[ 04 — Client Words ]</Overline>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tighter mt-6">What our clients say.</h2>
          </Reveal>
          {testimonials && testimonials.length > 0 ? (
            <ReviewSlider testimonials={testimonials} />
          ) : (
            <Reveal className="mt-14">
              <div className="border border-black/10 p-8 md:p-12 max-w-2xl" data-testid="testimonials-pending">
                <p className="text-graphite/60 leading-relaxed">
                  We are currently gathering reviews from recently completed projects, and we publish them only
                  with our clients’ permission. In the meantime, our genuine Google reviews are available below.
                </p>
              </div>
            </Reveal>
          )}
          <Reveal className="mt-8">
            <a
              href={settings?.google_reviews_url || "https://www.google.com/maps/place/NOOR+DESIGN+%26+BUILD+LTD/@50.9603712,-3.0532797,17z/data=!4m6!3m5!1s0x47906c5211e0788b:0xb8653868439a0e3!8m2!3d50.9603712!4d-3.0532797!16s%2Fg%2F11y_43zmmk"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-reviews-button"
              className="group inline-flex items-center gap-3 border border-graphite/25 hover:border-graphite px-6 py-3.5 text-sm font-semibold tracking-wide transition-colors duration-300"
            >
              Read our Google reviews <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* ---------------- AREAS ---------------- */}
      <section data-testid="areas-section" className="bg-charcoal text-white">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28">
          <Reveal className="max-w-2xl">
            <Overline light>[ 05 — Where We Work ]</Overline>
            <h2 className="font-display font-light text-4xl sm:text-5xl tracking-tighter mt-6 leading-[1.08]">
              Bristol, Bath, Somerset, Devon &amp; Surrounding Areas.
            </h2>
            <p className="text-white/55 leading-relaxed mt-8 text-base md:text-lg">
              We concentrate on a defined area so every project gets proper attention. As{" "}
              <Link to="/areas-we-cover/builders-bristol" data-testid="areas-bristol-link" className="text-white/80 underline decoration-white/25 underline-offset-4 hover:text-ember hover:decoration-ember transition-colors duration-300">
                builders in Bristol
              </Link>
              , much of our work is across the city and its neighbouring towns, with growing coverage across{" "}
              <Link to="/areas-we-cover/builders-devon" data-testid="areas-devon-link" className="text-white/80 underline decoration-white/25 underline-offset-4 hover:text-ember hover:decoration-ember transition-colors duration-300">
                Devon
              </Link>
              . Larger projects outside the standard area may be considered following an initial consultation.
            </p>
            <Link to="/areas-we-cover" data-testid="areas-view-all-link" className="group inline-flex items-center gap-3 mt-10 text-sm font-semibold text-white/70 hover:text-ember transition-colors duration-300">
              Explore local pages <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------------- QUALIFICATION ---------------- */}
      <section data-testid="qualification-section" className="bg-cream blueprint-grid-light">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>[ 06 — Your Project ]</Overline>
              <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.05]">
                Planning a Serious Home Transformation?
              </h2>
              <p className="text-graphite/60 text-base md:text-lg leading-relaxed mt-8 max-w-xl">
                We work best with homeowners who value quality, clear communication and professional project
                delivery. Tell us about your plans — we review every enquiry for suitability, location, budget
                and our current availability before arranging a site visit.
              </p>
              <Link
                to="/request-a-consultation"
                data-testid="qualification-form-button"
                className="btn-sheen group inline-flex items-center gap-3 mt-10 bg-graphite text-white px-8 py-4 font-semibold tracking-wide text-sm hover:bg-ember transition-colors duration-300"
              >
                Start your consultation request
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/cost-calculator"
                data-testid="qualification-calculator-link"
                className="group inline-flex items-center gap-3 mt-4 text-sm font-semibold text-graphite hover:text-ember transition-colors duration-300"
              >
                Or estimate your project cost first <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <Reveal delay={0.15}>
              <div className="border border-black/10 p-2">
                <img
                  src={TEAM_IMG}
                  alt="Architectural drawings being reviewed on a site workbench"
                  className="w-full aspect-[4/5] object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <RelatedInsights title="From the Insights Journal" keywords={["bristol", "bath", "somerset", "cost", "planning"]} />
      <CtaBand />
    </div>
  );
}
